import React, { useState, useEffect } from 'react';
import './PlanList.css';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import { useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import {FiChevronDown,FiRefreshCw } from 'react-icons/fi';

const PlanList = () => {
  const { userData } = useUser();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [Error, setError] = useState(null);


  const apiUrl = process.env.REACT_APP_API_ADMIN_PLAYING_PLAN_LIST;
  const headers = {
    'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
    'Content-Type': 'application/json',
  };

  const fetchPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: headers,
      });

      const result = await response.json();

      if (result.success && Array.isArray(result.data.plans)) {
        const sportsMap = result.data.sports || {};

        const mappedPlans = result.data.plans.map((plan) => {
          const sportIds = new Set();
        
          plan.validityBlocks?.forEach((block) => {
            if (block.sportId) {
              sportIds.add(block.sportId);
            }
            if (Array.isArray(block.sports)) {
              block.sports.forEach((s) => {
                if (s.sportId) {
                  sportIds.add(s.sportId);
                }
              });
            }
          });
        
          const sportNames = Array.from(sportIds)
            .map((id) => sportsMap[id] || `Sport ${id}`)
            .join(', ') || "N/A";
        
          const validity = plan.validityBlocks
            ?.map(v => `${v.validityInMonths} month`)
            .join(', ') ?? 'N/A';
        
          const price = plan.validityBlocks
            ?.map(v => v.price !== null ? `₹${v.price}` : 'N/A')
            .join(', ') ?? 'N/A';
        
          const strikeThroughPrice = plan.validityBlocks
            ?.map(v => v.strikeThroughPrice !== null ? `₹${v.strikeThroughPrice}` : 'N/A')
            .join(', ') ?? 'N/A';
        
          return {
            id: plan.id,
            name: plan.name,
            planType: plan.type,
            sport: sportNames,
            validity,
            gstInclusive: plan.gstInclusive ? 'Yes' : 'No',
            allCentreAccess: plan.allCentreAccess ? 'Yes' : 'No',
            benefitsAdded: plan.planBenefitsId ? 'Yes' : 'No',
            tagline: plan.tagline,
            price,
            strikeThroughPrice,
          };
        });
        

        setPlans(mappedPlans);
      } else {
        throw new Error(result.success_message || "Failed to fetch plans.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    fetchPlans();
  }, []);

  const header = [
    { key: 'name', label: 'Plan Name' },
    { key: 'planType', label: 'Plan Type' },
    { key: 'sport', label: 'Sports' },
    { key: 'validity', label: 'Validity' },
    { key: 'allCentreAccess', label: 'All Centre Acsess' },
    { key: 'tagline', label: 'Tagline' },
    { key: 'price', label: 'Price' },
    { key: 'strikeThroughPrice', label: 'Strike Through Price' },

  ];

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const navigate = useNavigate();

  const handleDelete = async (planId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });
  
    if (result.isConfirmed) {
      try {
        const url = `${process.env.REACT_APP_API_ADMIN_DELETE_PLAYING_PLAN}?id=${planId}`;
  
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "x-authorization": process.env.REACT_APP_AUTH_TOKEN,
            "Content-Type": "application/json",
          },
        });
  
        if (response.ok) {
          setPlans((prev) => prev.filter((plan) => plan.id !== planId)); 
  
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: "The plan has been deleted.",
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error!",
            text: "Failed to delete the plan.",
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
          });
        }
      } catch (error) {
        Swal.fire("Error!", "Something went wrong.", "error");
      }
    }
  };
  
  const handlePlanList = (event) => {
    const url = `/newPlan`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };
  const filteredPlans = plans.filter((plan) => {
    const searchLower = searchTerm.toLowerCase();

    return (
      plan.planType?.toLowerCase().includes(searchLower) ||
      plan.sport?.toLowerCase().includes(searchLower) ||
      plan.name?.toLowerCase().includes(searchLower)
    );
  });

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortData = (data) => {
    const { key, direction } = sortConfig;
    if (!key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[key] ?? '';
      let bValue = b[key] ?? '';


      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();


      const numA = parseFloat(aValue);
      const numB = parseFloat(bValue);
      const isNumeric = !isNaN(numA) && !isNaN(numB);

      if (isNumeric) {
        aValue = numA;
        bValue = numB;
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };




  const sortedData = sortData(filteredPlans);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };
const handleEdit = (planId, event) => {
  const url = `/newPlan?planId=${planId}`;
  if (event?.metaKey || event?.ctrlKey) {
    window.open(url, "_blank");
  } else {
    navigate(url);
  }
};


  const totalEntries = filteredPlans.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  return (
    <div className="enrollment-container">

      <div className="search-create-container">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearch}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        <button
          className="create-button-new"
          onClick={handlePlanList}
        >
          <span className="create-button__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="23"
              height="23"
              className="create-button__svg"
            >
              <path
                d="M12 5v14M5 12h14"
                stroke="white"
                strokeWidth="2.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="create-button__label">
            Create Plan
          </span>
        </button>
      </div>
      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
        <option value={100}>100 items per page</option>
      </select>
      <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={fetchPlans}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>
      
      <div className="tt-table-container">
        {loading ? (
          <div className="tt-loading">
            <div className="tt-loading-spinner"></div>
            <p>Loading Planlist...</p>
          </div>
        ) : (
          <>
            <table className="tt-table">
              <thead>
                <tr>
                  {header.map(({ key, label }) => (
                    <th key={key} onClick={() => handleSort(key)}>
                      <div className="th-content">
                        <span>{label}</span>
                        <FiChevronDown
                          className={`sort-icon ${sortConfig.key === key ? 'active' : ''} ${sortConfig.direction === 'desc' ? 'desc' : ''}`}
                        />
                      </div>
                    </th>
                  ))}
                  {hasPrivilege(userData, 'PLAYING_PLAN', 'PLANS_LIST_MODIFY') && <th>Action</th>}
                </tr>
              </thead>

              <tbody>
                {currentRows.map((plan) => (
                  <tr key={`${plan.id}`}>
                    <td>{plan.name}</td>
                    <td>{plan.planType}</td>
                    <td>{plan.sport}</td>
                    <td>{plan.validity}</td>
                    <td>{plan.allCentreAccess}</td>
                    <td>{plan.tagline}</td>
                    <td>{plan.price}</td>
                    <td>{plan.strikeThroughPrice}</td>
                    {/* <td>{plan.benefitsAdded}</td> */}
                    {hasPrivilege(userData, 'PLAYING_PLAN', 'PLANS_LIST_MODIFY') && (
                      <td className="action-buttons" style={{borderBottom:'none'}}>
                        <button
                          className="edit-btn tooltip-btn"
                          onClick={(e) => handleEdit(plan.id, e)}
                          title="Edit"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="white"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M16.862 3.487a2.12 2.12 0 0 1 3.001 3.001L7.5 18.85l-4 1 1-4L16.862 3.487z"
                            />
                          </svg>
                          <span className="tooltip-text">Edit</span>
                        </button>

                        <button
                          className="delete-btn tooltip-btn"
                          onClick={() => handleDelete( plan.id)}
                          title="Delete"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="white"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z"
                            />
                          </svg>
                          <span className="tooltip-text">Delete</span>
                        </button>
                      </td>
                    )}


                  </tr>
                ))}
              </tbody>
            </table>
            {filteredPlans.length === 0 && (
              <div className="tt-empty-state">
                <p>No plans found</p>
              </div>
            )}
          </>
        )}
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default PlanList;
